import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Selector de concepto del catálogo.
 *
 * Es lo que convierte el texto libre del proveedor ("THC NAVIERA") en
 * un código comparable ("THC_DESTINO"). Se puede dejar vacío: un
 * concepto sin catalogar se guarda igual, sólo que no entra en la
 * comparación línea a línea.
 *
 * Se escribe a mano y no con Radix para que entre en una celda de tabla
 * sin romper el layout.
 */
export default function ConceptoCombobox({
  catalogo = [],
  value,
  onSelect,
  disabled = false,
}) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const ref = useRef(null);

  const seleccionado = useMemo(
    () => catalogo.find((c) => c.codigo === value) || null,
    [catalogo, value],
  );

  useEffect(() => {
    if (!abierto) return;
    const alClic = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', alClic);
    return () => document.removeEventListener('mousedown', alClic);
  }, [abierto]);

  // Se busca por código, nombre y alias: el operador suele escribir el
  // nombre que usa el proveedor, no el del catálogo.
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return catalogo.slice(0, 60);
    return catalogo
      .filter((c) =>
        [c.codigo, c.nombre, ...(c.aliases || [])]
          .filter(Boolean)
          .some((t) => String(t).toLowerCase().includes(q)),
      )
      .slice(0, 60);
  }, [catalogo, busqueda]);

  const elegir = (item) => {
    onSelect?.(item);
    setAbierto(false);
    setBusqueda('');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAbierto((v) => !v)}
        className={cn(
          'flex h-8 w-full items-center justify-between gap-1 rounded border border-input',
          'bg-background px-2 text-left text-xs',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={cn('truncate', !seleccionado && 'text-muted-foreground')}>
          {seleccionado ? seleccionado.codigo : 'sin catalogar'}
        </span>
        <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
      </button>

      {abierto && (
        <div className="absolute z-50 mt-1 w-[260px] rounded-md border bg-popover shadow-md">
          <div className="border-b p-1.5">
            <input
              autoFocus
              className="h-7 w-full rounded border border-input bg-background px-2 text-xs focus-visible:outline-none"
              placeholder="Buscar concepto o alias…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {seleccionado && (
              <button
                type="button"
                onClick={() => elegir(null)}
                className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent"
              >
                <X className="h-3 w-3" /> Quitar código
              </button>
            )}
            {filtrados.length === 0 && (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                Sin resultados
              </p>
            )}
            {filtrados.map((c) => (
              <button
                key={c.codigo}
                type="button"
                onClick={() => elegir(c)}
                className="flex w-full items-start gap-1.5 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
              >
                <Check
                  className={cn(
                    'mt-0.5 h-3 w-3 shrink-0',
                    c.codigo === value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{c.nombre}</span>
                  <span className="block truncate text-[10px] text-muted-foreground">
                    {c.codigo} · {c.grupo}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
